import { useCallback, useState } from 'react'
import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { showSuccess, showValidationMessage, formatApiError } from '@/lib/utils/notifications'
import type { UserDetail } from '../types'
import { useUserRole } from '@/hooks/useUserRole'
import { assignRole } from '../services/userManagementApi'

export function useUserManagementEditAssignProperties(
  userId: string | undefined,
  user: UserDetail | null,
  fetchUser: () => Promise<UserDetail | null>,
) {
  const { companyId: currentRoleCompanyId } = useUserRole()
  const [showAssignProps, setShowAssignProps] = useState(false)
  const [assignPropOptions, setAssignPropOptions] = useState<SearchableSelectOption[]>([])
  const [assignPropLoading, setAssignPropLoading] = useState(false)
  const [assignPropValue, setAssignPropValue] = useState<string | string[]>('')
  const [assignPropSubmitting, setAssignPropSubmitting] = useState(false)

  const loadAssignProperties = useCallback(async (q?: string) => {
    setAssignPropLoading(true)
    try {
      const params: Record<string, string> = { all_property: 'true' }
      if (q?.trim()) params.q = q.trim()
      const res = await apiClient.get<{ property_id: number; property_name: string }[]>(
        API_ENDPOINTS.ALL_PROPERTY,
        { params },
      )
      const list = Array.isArray(res.data) ? res.data : []
      setAssignPropOptions(
        list.map((property) => ({
          value: String(property.property_id),
          label: property.property_name,
        })),
      )
      if (list.length === 1) setAssignPropValue(String(list[0].property_id))
    } catch {
      setAssignPropOptions([])
    } finally {
      setAssignPropLoading(false)
    }
  }, [])

  const handleAssignPropSearch = useCallback(
    (q: string) => {
      if (q.length > 0 && q.length % 2 !== 0) return
      void loadAssignProperties(q)
    },
    [loadAssignProperties],
  )

  const resetAssignProperties = () => {
    setAssignPropValue('')
    setAssignPropOptions([])
  }

  const toggleAssignProperties = () => {
    const next = !showAssignProps
    setShowAssignProps(next)
    if (next) void loadAssignProperties()
    else resetAssignProperties()
  }

  const handleAssignPropSubmit = async () => {
    if (!userId || !assignPropValue) return
    const companyId = currentRoleCompanyId ?? user?.roles?.find((role) => role.role_name === 'ROLE_REGIONAL_MANAGER')?.company_id
    setAssignPropSubmitting(true)
    try {
      await assignRole(userId, {
        role_name: 'ROLE_REGIONAL_MANAGER',
        company_id: companyId ? Number(companyId) : undefined,
        property_id: Array.isArray(assignPropValue)
          ? assignPropValue.map((id) => Number(id))
          : Number(assignPropValue),
      })
      showSuccess('Property Assign Successfully!')
      setAssignPropValue('')
      setShowAssignProps(false)
      void fetchUser()
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Error')
    } finally {
      setAssignPropSubmitting(false)
    }
  }

  return {
    showAssignProps,
    setShowAssignProps,
    assignPropOptions,
    assignPropLoading,
    assignPropValue,
    setAssignPropValue,
    assignPropSubmitting,
    loadAssignProperties,
    handleAssignPropSearch,
    toggleAssignProperties,
    resetAssignProperties,
    handleAssignPropSubmit,
  }
}
